// src/components/Pricing/Pricing.js
import React, { useState, useEffect } from 'react';
import './Pricing.css';
import axios from 'axios';
import { useUserContext } from '../../contexts/UserContext'; // Importa o contexto
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/config";
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://http://https://southamerica-east1-drecenna.cloudfunctions.net/api';

const Pricing = () => {
  const { user, isAuthenticated } = useUserContext(); // Usa o contexto
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const navigate = useNavigate();

  // Efeito para garantir que o tema claro esteja sempre ativo nesta página
  useEffect(() => {
    document.body.classList.remove('dark-theme');
    return () => {};
  }, []);

  // Definir o email automaticamente se o usuário estiver autenticado
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setEmail(user.email);
    }
  }, [isAuthenticated, user]);

  const plans = [
    {
      id: 'personal',
      name: 'Pessoal',
      price: 'R$99',
      frequency: '/mês',
      features: [
        'Acesso às funcionalidades básicas',
        'Uso para 1 usuário',
        'Suporte por email'
      ],
      buttonText: 'Assinar Plano Pessoal',
      popular: false
    },
    {
      id: 'business',
      name: 'Empresarial',
      price: 'R$249',
      frequency: '/mês',
      features: [
        'Todas as funcionalidades do plano Pessoal',
        'Uso para até 5 usuários',
        'Suporte prioritário',
        'Relatórios avançados'
      ],
      buttonText: 'Assinar Plano Empresarial',
      popular: true
    },
    {
      id: 'business_pro',
      name: 'Empresarial Pro',
      price: 'R$349',
      frequency: '/mês',
      features: [
        'Todas as funcionalidades do plano Empresarial',
        'Uso para até 20 usuários',
        'Suporte 24/7',
        'API personalizada',
        'Consultoria dedicada'
      ],
      buttonText: 'Assinar Plano Empresarial Pro',
      popular: false
    }
  ];

  const validateEmail = (emailToValidate) => { // Renomeado para evitar conflito
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(emailToValidate);
  };

  // Função para fazer logout
  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      alert('Erro ao fazer logout. Por favor, tente novamente.');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleSubscribe = async (planType) => {
    const userEmail = isAuthenticated ? user.email : email; // Renomeado para evitar conflito

    if (!userEmail) {
      setEmailError('Por favor, informe seu email');
      return;
    }

    if (!validateEmail(userEmail)) {
      setEmailError('Por favor, informe um email válido');
      return;
    }

    setEmailError('');
    setLoading(true);

    try {
      // *** MODIFICAÇÃO APLICADA AQUI ***
      const response = await axios.post(`${API_URL}/create-checkout-session`, {
        planType,
        email: userEmail,
        // Adicionar flag de renovação quando o usuário estiver autenticado
        isRenewal: isAuthenticated
      });
      // *** FIM DA MODIFICAÇÃO ***

      // Redirecionar para o checkout do Stripe
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Erro ao iniciar checkout:', error);
      alert('Erro ao iniciar o checkout. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pricing-container light-theme">
      <div className="pricing-header">
        <h1>Escolha o plano ideal para o seu negócio</h1>
        <p>Todos os planos incluem uma assinatura mensal com renovação automática</p>

        {isAuthenticated && (
          <div className="authenticated-user-container">
            <p className="user-status">
              Você está logado como: <strong>{user.email}</strong>
            </p>
            <button
              onClick={handleLogout}
              className="logout-button"
              disabled={loggingOut || loading}
            >
              {loggingOut ? "Saindo..." : "Sair e usar outra conta"}
            </button>
          </div>
        )}
      </div>

      {!isAuthenticated && (
        <div className="email-container">
          <div className="email-input-group">
            <label htmlFor="email">Informe seu email para continuar:</label>
            <input
              type="email"
              id="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={emailError ? 'error' : ''}
            />
            {emailError && <div className="error-message">{emailError}</div>}
          </div>
        </div>
      )}

      <div className="pricing-plans">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`pricing-card ${plan.popular ? 'popular' : ''}`}
          >
            {plan.popular && <div className="popular-badge">Mais Popular</div>}
            <h2>{plan.name}</h2>
            <div className="price">
              <span className="amount">{plan.price}</span>
              <span className="frequency">{plan.frequency}</span>
            </div>
            <ul className="features">
              {plan.features.map((feature, index) => (
                <li key={index}>✓ {feature}</li>
              ))}
            </ul>
            <button
              className="subscribe-button"
              onClick={() => handleSubscribe(plan.id)}
              disabled={loading}
            >
              {loading ? 'Processando...' :
               isAuthenticated ? `Renovar plano ${plan.name}` : plan.buttonText}
            </button>
          </div>
        ))}
      </div>

      <div className="pricing-footer">
        <p>Ao assinar, você concorda com nossos Termos de Serviço e Política de Privacidade</p>
        <p>Todos os pagamentos são processados de forma segura pelo Stripe</p>
      </div>
    </div>
  );
};

export default Pricing;